import React, { useRef, useMemo, useEffect, useState } from "react";

const SCROLL_SENSITIVITY = 0.001;
const MAX_ZOOM = 5;
const MIN_ZOOM = 0.1;

const ZoomImage = ({ image }) => {
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [draggind, setDragging] = useState(false);

    const touch = useRef({ x: 0, y: 0 });
    const initialDistance = useRef(0);
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const observer = useRef(null);
    const background = useMemo(() => new Image(), [image]);

    const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

    const handleWheel = (event) => {
        const { deltaY } = event;
        if (!draggind) {
            setZoom((zoom) =>
                clamp(zoom + deltaY * SCROLL_SENSITIVITY * -1, MIN_ZOOM, MAX_ZOOM)
            );
        }
    };

    const handleMouseMove = (event) => {
        if (draggind) {
            const { x, y } = touch.current;
            const { clientX, clientY } = event;
            setOffset({
                x: offset.x + (x - clientX),
                y: offset.y + (y - clientY),
            });
            touch.current = { x: clientX, y: clientY };
        }
    };

    const handleMouseDown = (event) => {
        const { clientX, clientY } = event;
        touch.current = { x: clientX, y: clientY };
        setDragging(true);
    };

    const handleMouseUp = () => setDragging(false);

    const handleTouchStart = (e) => {
        if (e.touches.length === 2) {
            initialDistance.current = Math.hypot(
                e.touches[0].pageX - e.touches[1].pageX,
                e.touches[0].pageY - e.touches[1].pageY
            );
        } else {
            const { clientX, clientY } = e.touches[0];
            touch.current = { x: clientX, y: clientY };
            setDragging(true);
        }
    };

    const handleTouchMove = (e) => {
        if (e.touches.length === 2) {
            const distance = Math.hypot(
                e.touches[0].pageX - e.touches[1].pageX,
                e.touches[0].pageY - e.touches[1].pageY
            );
            const difference = distance - initialDistance.current;
            setZoom((zoom) =>
                difference > 0
                    ? clamp(zoom + difference * SCROLL_SENSITIVITY, MIN_ZOOM, MAX_ZOOM) // fingers moved apart, zoom in
                    : clamp(zoom - Math.abs(difference) * SCROLL_SENSITIVITY, MIN_ZOOM, MAX_ZOOM) // fingers moved closer, zoom out
            );
            initialDistance.current = distance;
        } else if (draggind) {
            const { x, y } = touch.current;
            const { clientX, clientY } = e.touches[0];
            setOffset({
                x: offset.x + (x - clientX),
                y: offset.y + (y - clientY),
            });
            touch.current = { x: clientX, y: clientY };
        }
    };

    const handleTouchEnd = () => setDragging(false);

    const draw = () => {
        if (canvasRef.current) {
            const { width, height } = canvasRef.current;
            const context = canvasRef.current.getContext("2d");

            // Set canvas dimensions
            canvasRef.current.width = width;
            canvasRef.current.height = height;

            // Clear canvas and scale it
            context.translate(-offset.x, -offset.y);
            context.scale(zoom, zoom);
            context.clearRect(0, 0, width, height);

            // Make sure we're zooming to the center
            const x = (context.canvas.width / zoom - background.width) / 2;
            const y = (context.canvas.height / zoom - background.height) / 2;

            // Draw image
            context.drawImage(background, x, y);
        }
    };

    useEffect(() => {
        if(!canvasRef.current)
            return

        observer.current = new ResizeObserver((entries) => {
            const animationFrameId = window.requestAnimationFrame(() => {
                if(!canvasRef.current)
                    return

                entries.forEach(({ target }) => {
                    const { width, height } = background;
                    // If width of the container is smaller than image, scale image down
                    if (target.clientWidth < width) {
                        // Calculate scale
                        const scale = target.clientWidth / width;

                        // Redraw image
                        canvasRef.current.width = width * scale;
                        canvasRef.current.height = height * scale;
                        canvasRef.current
                            .getContext("2d")
                            .drawImage(background, 0, 0, width * scale, height * scale);
                    }
                });
            });

            return () => {
                window.cancelAnimationFrame(animationFrameId);
            }
        });
        observer.current.observe(containerRef.current);

        return () => {
            try {
                observer.current.unobserve(containerRef.current);
            } catch (e) {
                console.log(e)
            }
        }
    }, []);

    useEffect(() => {
        const preventDefault = (event) => event.preventDefault();

        containerRef.current.addEventListener('wheel', preventDefault, { passive: false });
        containerRef.current.addEventListener('touchmove', preventDefault, { passive: false });

        return () => {
            if(!containerRef.current)
                return;

            containerRef.current.removeEventListener('wheel', preventDefault);
            containerRef.current.removeEventListener('touchmove', preventDefault);
        }
    }, []);

    useEffect(() => {
        background.src = image;

        if (canvasRef.current) {
            background.onload = () => {
                // Get the image dimensions
                const { width, height } = background;
                canvasRef.current.width = width;
                canvasRef.current.height = height;

                // Set image as background
                canvasRef.current.getContext("2d").drawImage(background, 0, 0);
            };
        }
    }, [background]);

    useEffect(() => {
        draw();
    }, [zoom, offset]);

    return (
        <div ref={containerRef} style={{ backgroundColor: 'transparent' }} >
            <canvas
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onWheel={handleWheel}
                onMouseMove={handleMouseMove}
                ref={canvasRef}
            />
        </div>
    );
};

export default ZoomImage;